import Button from '@/common/components/Button'
import Modal from '@/common/components/Modal'
import Select from '@/common/components/Select'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'

import { CompanyData } from '@/features/companies/types/companies.type'
import { options as consumptionPasswordRestrictionsOptions } from '@/features/privateCompanies/constants/consumptionPasswordRestrictions.constant'
import { useUpdateSupplyPolitics } from '@/features/privateCompanies/hooks/politics/useUpdateSupplyPolitics'
import {
    UpdateSupplyPoliticsSchema,
    updateSupplyPoliticsSchema,
} from '@/features/privateCompanies/schemas/politics/updateSupplyPolitics.schema'

import { zodResolver } from '@hookform/resolvers/zod'
import { useState } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'

interface UpdateSupplyPoliticsModalProps {
    company?: CompanyData
}

export const UpdateSupplyPoliticsModal = ({ company }: UpdateSupplyPoliticsModalProps) => {
    const queryClient = useQueryClient()

    const [isOpen, setIsOpen] = useState(false)
    const [messages, setMessages] = useState<string[] | boolean>(false)

    const formMethods = useForm<UpdateSupplyPoliticsSchema>({
        resolver: zodResolver(updateSupplyPoliticsSchema),
        defaultValues: {
            consumption_password_update_restriction: company?.consumption_password_update_restriction || '',
        },
    })

    const {
        control,
        handleSubmit,
        formState: { errors },
    } = formMethods

    const { mutate: updateSupplyPolitics, isPending: updateSupplyPoliticsIsPending } = useUpdateSupplyPolitics({
        companyId: company?.id,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['company', company?.id] })
            setIsOpen(false)
        },
        onError: (error) => {
            setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao atualizar as políticas de abastecimento'])
        },
    })

    const onSubmit: SubmitHandler<UpdateSupplyPoliticsSchema> = (data) => {
        updateSupplyPolitics(data.consumption_password_update_restriction)
    }

    return (
        <Modal
            bodyClassName='overflow-visible'
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            Trigger={
                <Button
                    operationKlassToCheckPermission="Admin::Client::Companies::Update"
                    name="open_update_supply_politics_modal"
                    className="h-[2.125rem] text-sm text-white-800"
                >
                    Editar
                </Button>
            }
            title="Editando Políticas de Abastecimento"
            dataTestid="update_supply_politics"
            useCancellableFooter
            confirmButtonClassName="bg-blue-700 rounded-br-lg"
            cancelButtonClassName="bg-white-100 rounded-bl-lg"
            disabled={updateSupplyPoliticsIsPending}
            confirmButtonIsLoading={updateSupplyPoliticsIsPending}
            onConfirm={handleSubmit(onSubmit)}
            body={
                <div className="flex flex-col gap-y-4 text-white-800">
                    <div>
                        <p className="mb-2">
                            Senha para consumo com cartão (APP Condutor):
                        </p>

                        <Controller
                            name="consumption_password_update_restriction"
                            control={control}
                            render={({ field }) => (
                                <div>
                                    <Select
                                        placeholder="Selecione uma opção"
                                        options={consumptionPasswordRestrictionsOptions}
                                        value={consumptionPasswordRestrictionsOptions.find(option => option.value === field.value) || null}
                                        onChange={(selectedOption: any) => {
                                            field.onChange(selectedOption?.value || '')
                                        }}
                                        isDisabled={updateSupplyPoliticsIsPending}
                                    />
                                    {errors?.consumption_password_update_restriction?.message && (
                                        <p className="text-red-500 text-sm mt-1">
                                            {errors.consumption_password_update_restriction.message}
                                        </p>
                                    )}
                                </div>
                            )}
                        />
                    </div>

                    {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
                </div>
            }
        />
    )
}
